import React from 'react';

/**
 * Regex matching timecode formats:
 * - MM:SS or M:SS (e.g. 01:23, 1:23, 0:05)
 * - HH:MM:SS or H:MM:SS (e.g. 01:23:45, 1:05:30)
 * Uses lookaround to ensure clean boundaries even next to Thai text or punctuation.
 */
export const TIMECODE_REGEX = /(?<!\d)(?:(?:(\d{1,2}):)?([0-5]?\d):([0-5]\d))(?!\d)/g;

/**
 * Parses a timecode string (e.g. "01:23", "1:05:30") into total seconds.
 * Returns null if invalid.
 */
export function parseTimecodeToSeconds(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.trim().split(':').map(Number);
  if (parts.some((p) => isNaN(p) || p < 0)) return null;

  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    if (seconds >= 60) return null;
    return minutes * 60 + seconds;
  }
  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    if (minutes >= 60 || seconds >= 60) return null;
    return hours * 3600 + minutes * 60 + seconds;
  }
  return null;
}

/**
 * Component that parses timecodes in `text` and renders them as clickable links.
 * When a timecode is clicked, `onSeek(seconds)` is called.
 */
export const TimecodeText = ({ text, onSeek }) => {
  if (!text || typeof text !== 'string') return null;
  if (!onSeek) return <>{text}</>;

  const elements = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(TIMECODE_REGEX.source, 'g');

  while ((match = regex.exec(text)) !== null) {
    const matchText = match[0];
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      elements.push(text.slice(lastIndex, matchIndex));
    }

    const seconds = parseTimecodeToSeconds(matchText);
    if (seconds !== null) {
      elements.push(
        <button
          key={`${matchIndex}-${matchText}`}
          type="button"
          className="btn btn-link p-0 text-decoration-underline align-baseline fw-semibold"
          style={{ fontSize: 'inherit', verticalAlign: 'baseline', border: 'none', background: 'none' }}
          onClick={(e) => {
            e.preventDefault();
            onSeek(seconds);
          }}
          title={`Seek video to ${matchText}`}
        >
          {matchText}
        </button>
      );
    } else {
      elements.push(matchText);
    }

    lastIndex = matchIndex + matchText.length;
  }

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  return <>{elements}</>;
};

export default TimecodeText;
